import { Plane, MessageCircle, BadgeCheck } from "lucide-react";
import { Reveal } from "./Reveal";
import { waLink } from "./constants";

const ROLES = [
  {
    title: "Pilots & first officers",
    desc: "Flight pay, allowances and type-rating changes, presented properly to lenders who actually get them.",
  },
  {
    title: "Cabin crew",
    desc: "Seasonal contracts, sector pay and rostered hours — I know exactly how to evidence them.",
  },
  {
    title: "Engineers & ground staff",
    desc: "Shift allowances, overtime and contractor income, all counted the way they should be.",
  },
];

export default function Aviation() {
  return (
    <section id="aviation" data-testid="aviation-section" className="py-24 lg:py-32 relative overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-14 items-center">
        <div>
          <Reveal>
            <p className="font-mono text-xs tracking-[0.3em] uppercase text-amber-400/90 mb-5">
              My Specialism
            </p>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl tracking-tight font-medium text-white leading-tight">
              Work in aviation? You're my people.
            </h2>
            <p className="mt-6 text-base sm:text-lg text-slate-400 leading-relaxed">
              A huge part of my work is with aviation professionals — pilots,
              cabin crew, engineers and ground staff. I know how flight pay,
              allowances, per diems and rostered overtime look on paper, and
              more importantly, I know which lenders actually understand them.
            </p>
          </Reveal>
          <div className="mt-10 space-y-6">
            {ROLES.map((r, i) => (
              <Reveal key={r.title} delay={i * 0.08}>
                <div
                  data-testid={`aviation-role-${i}`}
                  className="flex gap-5 items-start"
                >
                  <span className="shrink-0 h-11 w-11 rounded-xl bg-amber-400/10 border border-amber-400/25 flex items-center justify-center">
                    <Plane size={18} className="text-amber-400" />
                  </span>
                  <div>
                    <h3 className="font-display text-lg font-medium text-white">
                      {r.title}
                    </h3>
                    <p className="mt-1 text-sm text-slate-400 leading-relaxed max-w-md">
                      {r.desc}
                    </p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>

        <Reveal delay={0.1}>
          <div
            data-testid="aviation-card"
            className="relative rounded-3xl border border-amber-400/25 bg-gradient-to-b from-[#0A3D50] to-[#052B38] p-8 lg:p-12 overflow-hidden"
          >
            <Plane
              aria-hidden="true"
              className="absolute -right-10 -top-10 h-48 w-48 text-amber-400/10 -rotate-12"
            />
            <p className="font-mono text-[11px] tracking-[0.25em] uppercase text-slate-400">
              The honest truth
            </p>
            <p className="mt-5 font-display text-2xl sm:text-3xl font-medium text-white leading-snug">
              Some lenders cap — or flat-out ignore — aviation allowances.
            </p>
            <p className="mt-5 text-base text-slate-300 leading-relaxed">
              I know the ones that don't. That can be the difference between
              the mortgage you've been offered and the mortgage you actually
              deserve.
            </p>
            <div className="mt-8 pt-6 border-t border-white/10 flex flex-wrap gap-2">
              {["Pilots", "Cabin Crew", "Engineers", "Ground Staff", "Contractors"].map((t) => (
                <span
                  key={t}
                  className="rounded-full border border-white/10 bg-white/5 px-3.5 py-1.5 font-mono text-[11px] tracking-wider uppercase text-slate-400"
                >
                  {t}
                </span>
              ))}
            </div>
            <a
              href={waLink("Hi Kieran, I work in aviation and I'd like some mortgage advice.")}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="aviation-whatsapp-cta"
              className="mt-9 w-full inline-flex items-center justify-center gap-2.5 rounded-full bg-[#25D366] px-7 py-3.5 text-sm font-semibold text-[#03222D] transition-colors duration-300 hover:bg-[#2be072]"
            >
              <MessageCircle size={17} />
              Chat to Me on WhatsApp
            </a>
            <p className="mt-5 flex items-center gap-2 text-xs text-slate-500">
              <BadgeCheck size={14} className="text-emerald-400" />
              Whole-of-market, so your options stay wide open
            </p>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
